import type { MinimalGlossaryTerm } from "@/app/(marketing)/(payload)/dictionary/_actions/glossary";
import { GlossaryText } from "@/components/common/glossary-text";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type {
  FundingDraft,
  NumericField,
} from "@/lib/calculators/acquisition-funding";

export type FundingFieldProps = {
  name: NumericField;
  label: string;
  hint?: string;
  terms?: MinimalGlossaryTerm[];
  unit: string;
  prefix: string;
  draft: FundingDraft;
  error?: string;
  onChange: (name: NumericField, value: string) => void;
};

/** A calculator amount/rate/term with its unit, explanation and validation. */
export function FundingField({
  name,
  label,
  hint,
  terms,
  unit,
  prefix,
  draft,
  error,
  onChange,
}: FundingFieldProps) {
  const id = `${prefix}-${name}`;
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium">
        {label}{" "}
        <span className="font-normal text-muted-foreground">({unit})</span>
      </Label>
      <Input
        id={id}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        spellCheck={false}
        className="h-12 text-base tabular-nums md:text-base"
        value={draft[name]}
        maxLength={22}
        aria-invalid={Boolean(error)}
        aria-describedby={
          [hint ? `${id}-hint` : "", error ? `${id}-error` : ""]
            .filter(Boolean)
            .join(" ") || undefined
        }
        onChange={(event) => onChange(name, event.target.value)}
      />
      {hint && (
        <p
          id={`${id}-hint`}
          className="text-xs leading-relaxed text-muted-foreground"
        >
          <GlossaryText text={hint} terms={terms} />
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
